import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeState {
	theme: Theme;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: Theme) => void;
}

const prefersDark = () =>
	typeof window !== "undefined" &&
	window.matchMedia("(prefers-color-scheme: dark)").matches;

function resolve(theme: Theme): ResolvedTheme {
	if (theme === "system") return prefersDark() ? "dark" : "light";
	return theme;
}

export const useTheme = create<ThemeState>()(
	persist(
		(set) => ({
			theme: "system",
			resolvedTheme: resolve("system"),
			setTheme: (theme) => set({ theme, resolvedTheme: resolve(theme) }),
		}),
		{
			name: "otd-theme",
			partialize: (state) => ({ theme: state.theme }),
		},
	),
);

function applyResolvedTheme(theme: Theme) {
	const resolved = resolve(theme);
	document.documentElement.classList.toggle("dark", resolved === "dark");
	if (useTheme.getState().resolvedTheme !== resolved) {
		useTheme.setState({ resolvedTheme: resolved });
	}
}

if (typeof document !== "undefined") {
	applyResolvedTheme(useTheme.getState().theme);
	useTheme.subscribe((state) => applyResolvedTheme(state.theme));
	window
		.matchMedia("(prefers-color-scheme: dark)")
		.addEventListener("change", () => {
			if (useTheme.getState().theme === "system") {
				applyResolvedTheme("system");
			}
		});
}
